import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import api, { imgUrl } from "@/lib/api";
import { Sun, Moon, Languages, ArrowRight, PackageCheck, Mail, Globe, Hand } from "lucide-react";

const LOGO = "/lapaz-logo.png";
const LOGO_WHITE = "/lapaz-logo-white.png";

const HERO = "https://images.unsplash.com/photo-1573498945275-98751e3f605f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const BANNER = "https://images.pexels.com/photos/32549949/pexels-photo-32549949.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

const CATEGORIES = [
  { key: "calzado", img: "https://images.unsplash.com/photo-1533867617858-e7b97e060509?crop=entropy&cs=srgb&fm=jpg&q=85&w=940" },
  { key: "ropa", img: "https://images.pexels.com/photos/21835299/pexels-photo-21835299.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940" },
  { key: "accesorios", img: "https://images.pexels.com/photos/9807760/pexels-photo-9807760.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940" },
];

export default function Landing() {
  const { t, lang, theme, toggleLang, toggleTheme } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/public/products").then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  const go = () => navigate(user ? "/catalogo" : "/login");

  return (
    <div className="min-h-screen bg-background">
      {/* Announcement */}
      <div className="bg-primary text-primary-foreground text-center text-[11px] sm:text-xs font-semibold uppercase tracking-[0.2em] py-2 px-4">
        {t("landingAnnounce")}
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/85 border-b px-4 sm:px-8 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="La Paz Internacional" className="h-9 sm:h-11 w-auto object-contain dark:hidden" />
          <img src={LOGO_WHITE} alt="La Paz Internacional" className="h-9 sm:h-11 w-auto object-contain hidden dark:block" />
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="landing-language-toggle" onClick={toggleLang} className="h-11 px-3 rounded-full border bg-card flex items-center gap-2 font-semibold text-sm hover:bg-muted">
            <Languages className="h-4 w-4" /> {lang === "es" ? "ES" : "EN"}
          </button>
          <button data-testid="landing-theme-toggle" onClick={toggleTheme} className="h-11 w-11 rounded-full border bg-card flex items-center justify-center hover:bg-muted">
            {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </button>
          <button
            data-testid="landing-enter-button"
            onClick={go}
            className="h-11 px-6 rounded-full bg-primary text-primary-foreground font-bold text-sm hover:opacity-90 active:scale-95 transition"
          >
            {t("enter")}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative h-[78vh] min-h-[520px] w-full overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/20" />
        <div className="relative h-full max-w-7xl mx-auto px-6 sm:px-8 flex flex-col justify-center">
          <span className="text-white/70 uppercase tracking-[0.35em] text-xs sm:text-sm font-semibold animate-fade-up">
            {t("heroKicker")}
          </span>
          <h1 className="mt-4 font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl animate-fade-up">
            {t("heroTitle")}
          </h1>
          <p className="mt-5 text-white/85 text-base sm:text-lg max-w-xl animate-fade-up">{t("heroSubtitle")}</p>
          <div className="mt-8 flex flex-wrap gap-3 animate-fade-up">
            <button
              data-testid="hero-explore-button"
              onClick={go}
              className="h-14 px-8 rounded-full bg-white text-black font-bold flex items-center gap-2 hover:gap-3 transition-all active:scale-95"
            >
              {t("exploreCatalog")} <ArrowRight className="h-5 w-5" />
            </button>
            <button
              onClick={() => navigate("/login")}
              className="h-14 px-8 rounded-full border border-white/50 text-white font-semibold hover:bg-white/10 transition"
            >
              {t("login")}
            </button>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display font-bold text-3xl sm:text-4xl">{t("shopByCategory")}</h2>
          <button onClick={go} className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all">
            {t("viewAll")} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
          {CATEGORIES.map((c, i) => (
            <button
              key={c.key}
              data-testid={`landing-category-${c.key}`}
              onClick={go}
              className="group relative rounded-3xl overflow-hidden aspect-[4/5] animate-fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <img src={c.img} alt={t(c.key)} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-6 text-left">
                <span className="text-white/70 uppercase tracking-[0.25em] text-[11px] font-semibold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="mt-1 flex items-center justify-between">
                  <h3 className="font-display font-bold text-white text-2xl">{t(c.key)}</h3>
                  <span className="h-10 w-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center text-white group-hover:bg-white group-hover:text-black transition">
                    <ArrowRight className="h-5 w-5" />
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Featured */}
      {featured.length > 0 && (
        <section className="bg-muted/40 py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="flex items-end justify-between mb-8">
              <h2 className="font-display font-bold text-3xl sm:text-4xl">{t("featuredTitle")}</h2>
              <button onClick={go} className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all">
                {t("viewAll")} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {featured.slice(0, 8).map((p, i) => (
                <button
                  key={p.id}
                  data-testid={`landing-featured-${p.id}`}
                  onClick={go}
                  className="group text-left rounded-2xl bg-card border overflow-hidden hover:shadow-lg transition-shadow animate-fade-up"
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  <div className="aspect-square bg-muted overflow-hidden">
                    {p.image && <img src={imgUrl(p.image)} alt="" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />}
                  </div>
                  <div className="p-4">
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                      {lang === "es" ? p.category_es : p.category_en}
                    </span>
                    <h3 className="mt-1 font-semibold text-sm line-clamp-1">{lang === "es" ? p.name_es : p.name_en}</h3>
                    <div className="mt-1 font-mono font-bold text-primary text-lg">${p.price.toFixed(2)}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Value props */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
        <h2 className="font-display font-bold text-3xl sm:text-4xl text-center mb-12">{t("whyTitle")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: PackageCheck, t: "val1t", d: "val1d" },
            { icon: Mail, t: "val2t", d: "val2d" },
            { icon: Globe, t: "val3t", d: "val3d" },
            { icon: Hand, t: "val4t", d: "val4d" },
          ].map((v) => (
            <div key={v.t} className="rounded-2xl border bg-card p-6">
              <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <v.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-4 font-display font-bold text-lg">{t(v.t)}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{t(v.d)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Campaign banner */}
      <section className="relative h-[42vh] min-h-[340px] overflow-hidden">
        <img src={BANNER} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/70" />
        <div className="relative h-full max-w-7xl mx-auto px-6 sm:px-8 flex flex-col items-start justify-center">
          <h2 className="font-display font-extrabold text-white text-3xl sm:text-5xl max-w-2xl">{t("bannerTitle")}</h2>
          <p className="mt-3 text-white/85 text-lg max-w-lg">{t("bannerText")}</p>
          <button
            data-testid="banner-enter-button"
            onClick={go}
            className="mt-7 h-14 px-8 rounded-full bg-white text-black font-bold flex items-center gap-2 hover:gap-3 transition-all active:scale-95"
          >
            {t("enter")} <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground text-background dark:bg-card dark:text-foreground border-t">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-12 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img src={LOGO_WHITE} alt="La Paz Internacional" className="h-10 w-auto object-contain dark:hidden" />
            <img src={LOGO} alt="La Paz Internacional" className="h-10 w-auto object-contain hidden dark:block" />
          </div>
          <div className="text-center sm:text-right">
            <div className="font-display font-bold text-lg">Appomax B2B</div>
            <p className="text-sm opacity-70">{t("footerTagline")}</p>
            <p className="text-xs opacity-50 mt-2">
              © {new Date().getFullYear()} La Paz Internacional, S.A. — {t("footerRights")}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
